import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, CardBody, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../AbstractElements';
import { fetchConsolidatedReport, setConsolidatedFilter, setConsolidatedRange } from '../../store/teacherHoursSlice';

/**
 * ClassReport — Consolidated class hours report for ALL teachers.
 * Shows total actual vs estimated hours per teacher in a single table.
 * Supports Today / This Week / This Month / Custom date range.
 * Exports to CSV and Print (PDF).
 */
const ClassReport = () => {
  const dispatch = useDispatch();
  const { consolidatedData, isConsolidatedLoading, consolidatedFilter } =
    useSelector((state) => state.teacherHours);

  const [localStart, setLocalStart] = useState('');
  const [localEnd,   setLocalEnd]   = useState('');

  useEffect(() => {
    dispatch(fetchConsolidatedReport('week'));
  }, []);

  const handleFilter = (f) => {
    dispatch(setConsolidatedFilter(f));
    if (f !== 'custom') dispatch(fetchConsolidatedReport(f));
  };

  const handleCustomApply = () => {
    if (!localStart || !localEnd) return;
    dispatch(setConsolidatedRange({ start: localStart, end: localEnd }));
    dispatch(fetchConsolidatedReport('custom', localStart, localEnd));
  };

  // ── CSV Export ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!consolidatedData?.rows?.length) return;
    const rows = [
      ['#', 'Teacher', 'Subject', 'Classes Count', 'Class Details (Name [Act/Est])', 'Actual Hours', 'Estimated Hours'],
      ...consolidatedData.rows.map((r, i) => [
        i + 1, r.name, r.subject || '-', r.totalClasses, r.classNames ? r.classNames.map(c => typeof c === 'object' ? `${c.title} (Act: ${c.actualHours} / Est: ${c.estimatedHours})` : c).join('; ') : '-', r.actualHours, r.estimatedHours,
      ]),
      [],
      ['', 'GRAND TOTAL', '', consolidatedData.summary.grandClasses, '',
        consolidatedData.summary.grandActualHours, consolidatedData.summary.grandEstimatedHours],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_report_${consolidatedFilter}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print ───────────────────────────────────────────────────────────────────
  const exportPrint = () => {
    if (!consolidatedData?.rows?.length) return;
    const rows = consolidatedData.rows.map((r, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#fafbfd'}">
        <td>${i + 1}</td>
        <td><strong>${r.name}</strong></td>
        <td style="color:#6e707e">${r.subject || '-'}</td>
        <td style="text-align:center"><span style="background:#e8f4fd;color:#2e86de;border-radius:10px;padding:2px 10px;font-weight:700">${r.totalClasses}</span></td>
        <td>${r.classNames ? r.classNames.map(c => typeof c === 'object' ? `<div>• ${c.title} <span style="color:#4e73df;font-size:11px;font-weight:700">(${c.actualHours} act / ${c.estimatedHours} est)</span></div>` : `<div>• ${c}</div>`).join('') : '-'}</td>
        <td style="text-align:right;color:#4e73df;font-weight:700">${r.actualHours}</td>
        <td style="text-align:right;color:#6c757d">${r.estimatedHours}</td>
      </tr>`).join('');
    const s = consolidatedData.summary;
    const html = `<!DOCTYPE html><html><head><title>Class Report</title>
    <style>
      body{font-family:Arial,sans-serif;padding:24px;color:#333}
      h2{margin-bottom:4px}.stats{display:flex;gap:16px;margin:16px 0 20px}
      .stat{background:#f1f3ff;border-radius:8px;padding:10px 18px;text-align:center}
      .stat .v{font-size:20px;font-weight:800;color:#4e73df}.stat .l{font-size:11px;color:#666}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th{background:#f8f9fc;padding:9px 12px;text-align:left;border-bottom:2px solid #e3e6f0;font-size:11px;font-weight:700;color:#5a5c69}
      td{padding:9px 12px;border-bottom:1px solid #f0f0f0}
      tfoot tr{background:#f8f9fc;font-weight:700;border-top:2px solid #e3e6f0}
    </style></head><body>
    <h2>📊 Class Report — All Teachers</h2>
    <p style="color:#6c757d;margin:0 0 12px">Period: ${consolidatedFilter.toUpperCase()}</p>
    <div class="stats">
      <div class="stat"><div class="v">${s.totalTeachers}</div><div class="l">Total Teachers</div></div>
      <div class="stat"><div class="v">${s.activeTeachers}</div><div class="l">Active Teachers</div></div>
      <div class="stat"><div class="v">${s.grandClasses}</div><div class="l">Total Classes</div></div>
      <div class="stat"><div class="v">${s.grandActualHours}</div><div class="l">Actual Hours</div></div>
      <div class="stat"><div class="v">${s.grandEstimatedHours}</div><div class="l">Estimated Hours</div></div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Teacher</th><th>Subject</th><th style="text-align:center">Classes</th><th>Class Details</th><th style="text-align:right">Actual Hours</th><th style="text-align:right">Estimated Hours</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="3">GRAND TOTAL</td><td style="text-align:center">${s.grandClasses}</td><td></td><td style="text-align:right;color:#4e73df">${s.grandActualHours}</td><td style="text-align:right;color:#6c757d">${s.grandEstimatedHours}</td></tr></tfoot>
    </table>
    <p style="margin-top:12px;font-size:11px;color:#999">Generated: ${new Date().toLocaleString('en-IN')}</p>
    <script>window.onload=()=>{window.print();window.close()}</script>
    </body></html>`;
    const w = window.open('', '_blank', 'width=960,height=720');
    w.document.write(html);
    w.document.close();
  };

  const filters = [
    { key: 'today', label: 'Today' },
    { key: 'week',  label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  const s = consolidatedData?.summary;

  return (
    <Fragment>
      <Breadcrumbs mainTitle='Class Report' parent='Teachers' title='Class Report' />
      <Container fluid={true}>
        <Row>
          <Col sm='12'>
            <Card>
              <CardBody>

                {/* ── Header row: filters + exports ── */}
                <div className='d-flex align-items-center flex-wrap gap-2 mb-3'>
                  <h5 className='mb-0 me-2' style={{ fontWeight: 700, color: '#2c3e50' }}>
                    📊 Consolidated Class Hours — All Teachers
                  </h5>
                  <div className='d-flex gap-2 flex-wrap ms-auto align-items-center'>
                    {filters.map(f => (
                      <button key={f.key} onClick={() => handleFilter(f.key)}
                        style={{
                          padding: '5px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                          fontWeight: 600, fontSize: 13,
                          background: consolidatedFilter === f.key ? '#4e73df' : '#eaecf4',
                          color:      consolidatedFilter === f.key ? '#fff'    : '#5a5c69',
                        }}>
                        {f.label}
                      </button>
                    ))}
                    <button onClick={exportCSV} disabled={!consolidatedData?.rows?.length} style={exportBtn('#1cc88a')}>⬇ CSV</button>
                    <button onClick={exportPrint} disabled={!consolidatedData?.rows?.length} style={exportBtn('#36b9cc')}>🖨 Print</button>
                  </div>
                </div>

                {/* ── Custom date range ── */}
                {consolidatedFilter === 'custom' && (
                  <div className='d-flex align-items-center gap-2 mb-3 p-3'
                    style={{ background: '#f0f4ff', borderRadius: 8, border: '1px solid #d0d8f0' }}>
                    <label style={labelStyle}>From</label>
                    <input type='date' value={localStart} onChange={e => setLocalStart(e.target.value)}
                      style={dateInput} max={localEnd || undefined} />
                    <label style={labelStyle}>To</label>
                    <input type='date' value={localEnd} onChange={e => setLocalEnd(e.target.value)}
                      style={dateInput} min={localStart || undefined} max={new Date().toISOString().split('T')[0]} />
                    <button onClick={handleCustomApply} disabled={!localStart || !localEnd}
                      style={{ ...exportBtn('#4e73df'), opacity: (!localStart || !localEnd) ? 0.5 : 1 }}>
                      Apply
                    </button>
                  </div>
                )}

                {/* ── Summary stat cards ── */}
                {s && (
                  <div className='row g-3 mb-4'>
                    {[
                      { color: '#4e73df', icon: '👩‍🏫', value: s.totalTeachers,       label: 'Total Teachers' },
                      { color: '#36b9cc', icon: '✅',   value: s.activeTeachers,      label: 'Active This Period' },
                      { color: '#1cc88a', icon: '📚',   value: s.grandClasses,        label: 'Total Classes' },
                      { color: '#f6c23e', icon: '🕐',   value: s.grandActualHours,    label: 'Total Actual Hours' },
                      { color: '#858796', icon: '📋',   value: s.grandEstimatedHours, label: 'Total Estimated Hours' },
                    ].map((c, i) => (
                      <div className='col' key={i}>
                        <div style={cardStyle(c.color)}>
                          <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
                          <div style={{ fontSize: 20, fontWeight: 800 }}>{c.value}</div>
                          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 3 }}>{c.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Loading ── */}
                {isConsolidatedLoading ? (
                  <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 240 }}>
                    <Spinner color='primary' />
                  </div>

                ) : !consolidatedData?.rows?.length ? (
                  <div className='text-center' style={{ padding: '48px 0', color: '#b7b9cc' }}>
                    <i className='fa fa-bar-chart fa-3x mb-3' style={{ display: 'block' }}></i>
                    <p style={{ fontWeight: 600, fontSize: 15 }}>No class data for this period</p>
                  </div>

                ) : (
                  /* ── Consolidated Table ── */
                  <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e3e6f0' }}>
                    <table className='table mb-0' style={{ fontSize: 13 }}>
                      <thead style={{ background: '#f8f9fc' }}>
                        <tr>
                          <th style={{ ...th, width: '4%' }}>#</th>
                          <th style={{ ...th, width: '18%' }}>Teacher Name</th>
                          <th style={{ ...th, width: '10%' }}>Subject</th>
                          <th style={{ ...th, textAlign: 'center', width: '6%' }}>Classes</th>
                          <th style={{ ...th, width: '38%' }}>Class Details (Actual / Estimated)</th>
                          <th style={{ ...th, textAlign: 'right', width: '10%' }}>
                            Actual Hours <span style={badge('#4e73df')}>Real</span>
                          </th>
                          <th style={{ ...th, textAlign: 'right', width: '10%' }}>
                            Estimated <span style={badge('#6c757d')}>Planned</span>
                          </th>
                          <th style={{ ...th, textAlign: 'center', width: '12%' }}>Attendance %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consolidatedData.rows.map((row, i) => {
                          const pct = row.totalEstimatedMinutes > 0
                            ? Math.round((row.totalActualMinutes / row.totalEstimatedMinutes) * 100)
                            : 0;
                          const pctColor = pct >= 90 ? '#1cc88a' : pct >= 60 ? '#f6c23e' : '#e74a3b';
                          return (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfd' }}>
                              <td style={{ ...td, color: '#b7b9cc', fontWeight: 600 }}>{i + 1}</td>
                              <td style={{ ...td, fontWeight: 700 }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 28, height: 28, borderRadius: '50%', background: '#e8eeff',
                                  color: '#4e73df', fontWeight: 800, fontSize: 11, marginRight: 8,
                                }}>
                                  {row.name?.charAt(0)?.toUpperCase()}
                                </span>
                                {row.name}
                              </td>
                              <td style={{ ...td, color: '#6e707e' }}>{row.subject || '—'}</td>
                              <td style={{ ...td, textAlign: 'center' }}>
                                {row.totalClasses > 0 ? (
                                  <span style={{ background: '#e8f4fd', color: '#2e86de', borderRadius: 12, padding: '2px 10px', fontWeight: 600 }}>
                                    {row.totalClasses}
                                  </span>
                                ) : (
                                  <span style={{ color: '#b7b9cc' }}>—</span>
                                )}
                              </td>
                              <td style={{ ...td, color: '#6e707e' }}>
                                {row.classNames && row.classNames.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    {row.classNames.map((c, idx) => {
                                      const titleText = typeof c === 'object' ? c.title : c;
                                      const timeDetails = typeof c === 'object' ? `(Act: ${c.actualHours} / Est: ${c.estimatedHours})` : '';
                                      return (
                                        <span key={idx} className="badge bg-light text-dark text-start d-flex justify-content-between align-items-center" style={{ border: '1px solid #eaecf4', whiteSpace: 'normal', fontSize: '11px', padding: '4px 8px', fontWeight: 500 }}>
                                          <span>• {titleText}</span>
                                          {timeDetails && <span className="text-muted ms-2 font-monospace" style={{ fontSize: '10px' }}>{timeDetails}</span>}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span style={{ color: '#b7b9cc' }}>—</span>
                                )}
                              </td>
                              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#4e73df' }}>
                                {row.totalClasses > 0 ? row.actualHours : <span style={{ color: '#b7b9cc' }}>—</span>}
                              </td>
                              <td style={{ ...td, textAlign: 'right', color: '#6c757d' }}>
                                {row.totalClasses > 0 ? row.estimatedHours : <span style={{ color: '#b7b9cc' }}>—</span>}
                              </td>
                              <td style={{ ...td, textAlign: 'center' }}>
                                {row.totalClasses > 0 ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                    <div style={{ flex: 1, height: 6, background: '#eaecf4', borderRadius: 4, minWidth: 60 }}>
                                      <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: pctColor, borderRadius: 4, transition: 'width 0.4s' }} />
                                    </div>
                                    <span style={{ fontWeight: 700, color: pctColor, minWidth: 36 }}>{pct}%</span>
                                  </div>
                                ) : <span style={{ color: '#b7b9cc' }}>—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f8f9fc', borderTop: '2px solid #e3e6f0' }}>
                           <td style={td} colSpan={3}><strong>GRAND TOTAL</strong></td>
                           <td style={{ ...td, textAlign: 'center' }}>
                             <span style={{ background: '#c8e6fd', color: '#2e86de', borderRadius: 12, padding: '2px 10px', fontWeight: 700 }}>
                               {s?.grandClasses}
                             </span>
                           </td>
                           <td style={td}></td>
                           <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#4e73df', fontSize: 14 }}>
                             {s?.grandActualHours}
                           </td>
                           <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#6c757d', fontSize: 14 }}>
                             {s?.grandEstimatedHours}
                           </td>
                           <td style={td}></td>
                         </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

// ─── Style helpers ──────────────────────────────────────────────────────────
const cardStyle = (color) => ({
  background: color, borderRadius: 10, padding: '14px 10px',
  textAlign: 'center', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
});
const exportBtn = (color) => ({
  padding: '5px 14px', borderRadius: 6, border: 'none',
  background: color, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer',
});
const badge = (color) => ({
  display: 'inline-block', marginLeft: 5, fontSize: 9, fontWeight: 700,
  background: color, color: '#fff', padding: '1px 5px', borderRadius: 10,
  letterSpacing: 0.3, verticalAlign: 'middle',
});
const dateInput = { border: '1px solid #ced4da', borderRadius: 6, padding: '5px 10px', fontSize: 13 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#5a5c69', whiteSpace: 'nowrap' };
const th = { padding: '10px 14px', fontWeight: 700, fontSize: 12, color: '#5a5c69', borderBottom: '2px solid #e3e6f0' };
const td = { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };

export default ClassReport;
